/* ============================================================
 * Push Notifications (Capacitor Firebase Messaging) — native-only helper
 * ------------------------------------------------------------
 * This module is a NO-OP on the web build. Every entry point first
 * lazy-loads @capacitor/core and bails out unless we're running inside
 * a real native shell (Capacitor.isNativePlatform()). That means:
 *   - The web bundle never pulls the push SDK into the critical path
 *     (dynamic import() => its own chunk, only fetched on native).
 *   - Calling these from App.js on the web is completely safe.
 *
 * Uses @capacitor-firebase/messaging so iOS and Android BOTH register an
 * FCM token (the backend dispatches via firebase-admin). Unlike the old
 * @capacitor/push-notifications, the token is fetched imperatively via
 * getToken() (no register()/registration event); addListener('tokenReceived')
 * handles refreshes. Notification payloads are nested under event.notification.
 *
 * EXTERNAL CONTRACTS (kept stable so App.js needs zero changes):
 *   - exports: initPushNotifications(), disposePushNotifications(),
 *     removeTokenFromBackend(token, loginToken), deleteDeviceToken()
 *   - SESSION BINDING (owner ruling 2026-11-03): the backend binds each
 *     registered token to the login session (sid) and delivers only while that
 *     session is live. Logout goes through POST /api/logout (App.js
 *     handleLogout: revoke + token removal in one request, awaited), then
 *     deleteDeviceToken() invalidates the token ON the device so a forced
 *     logout (401) leaves nothing behind either.
 *   - window CustomEvent: 'crm:open-lead' (tap). NOTE: 'crm:push-received' is no
 *     longer emitted — option (b) shows the system notification only in foreground
 *     (no in-app banner). App.js's crm:push-received listener simply never fires.
 *   - localStorage tap keys: crm_pending_lead / crm_pending_lead_type / crm_pending_lead_status
 *   - POST /api/users/push-token { token, platform, deviceId }
 *
 * Backend contract: POST /api/users/push-token  { token, platform, deviceId }
 * Auth: Bearer token read from localStorage 'crm_aro_session' (same shape
 * the rest of App.js uses: { user, token, csrfToken }).
 * ============================================================ */

var API = (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL
  : "https://crm-aro-backend-production.up.railway.app";

// Handles to the registered listeners so disposePushNotifications() can
// remove them. Capacitor's addListener returns a promise of a handle with
// a .remove() method; we stash the promises and await them on cleanup.
var _listeners = [];
var _initialized = false;

// Cache the most recent FCM token so logout can deregister it (Capacitor
// exposes no synchronous getter). Mirrored to localStorage so a cold logout
// after a fresh module load can still find the token.
var _lastToken = "";
try {
  _lastToken = (typeof window !== "undefined" && window.localStorage)
    ? (localStorage.getItem("crm_push_token") || "") : "";
} catch (e) { _lastToken = ""; }

// Lazy-load @capacitor/core and report whether we're on a native platform.
// Returns the Capacitor object on native, or null on web / if unavailable.
async function _getNativeCapacitor() {
  try {
    var mod = await import("@capacitor/core");
    var Capacitor = mod && (mod.Capacitor || (mod.default && mod.default.Capacitor));
    if (Capacitor && typeof Capacitor.isNativePlatform === "function" && Capacitor.isNativePlatform()) {
      return Capacitor;
    }
  } catch (e) { /* core not present (pure web build) — fall through */ }
  return null;
}

// Coerce an FCM data payload (typed `unknown` by the plugin) to a plain object
// so reads of .type/.leadId/.status never throw on a null/string value.
function _asObject(v) {
  return (v && typeof v === "object") ? v : {};
}

// ===== Option (b) foreground display: system notification only =====
// iOS shows the OS banner in foreground via presentationOptions ["alert","badge",
// "sound"] (capacitor.config.ts). Android does NOT auto-display a foreground FCM
// notification, so we post a local notification mirroring it via
// @capacitor/local-notifications (Android ONLY — doing it on iOS would duplicate
// the OS banner). No in-app banner is shown in foreground on either platform.
var CRM_PUSH_CHANNEL_ID = "crm_push";
var _channelReady = false;
var _localNotifSeq = 1000; // monotonic id source for scheduled local notifications

// Apply a tapped notification's data to the durable open-lead bridge. Shared by
// the FCM tap handler AND the Android local-notification tap handler so both
// routes behave identically (App.js reads these on mount / listens for the event).
function _routeOpenLead(rawData) {
  try {
    var d = _asObject(rawData);
    var lid = d.leadId ? String(d.leadId) : "";
    if (!lid) return;
    try {
      localStorage.setItem("crm_pending_lead", lid);
      localStorage.setItem("crm_pending_lead_type", d.type ? String(d.type) : "");
      localStorage.setItem("crm_pending_lead_status", d.status ? String(d.status) : "");
    } catch (e) {}
    if (typeof window !== "undefined" && window.dispatchEvent) {
      try {
        window.dispatchEvent(new CustomEvent("crm:open-lead", {
          detail: { leadId: lid, type: d.type || "", status: d.status || "" }
        }));
      } catch (e) {}
    }
  } catch (e) { /* never let tap routing throw */ }
}

// Lazy-load @capacitor/local-notifications. Returns the plugin or null. Only the
// Android path calls this; web/iOS never invoke it.
async function _getLocalNotifications() {
  try {
    var mod = await import("@capacitor/local-notifications");
    return (mod && (mod.LocalNotifications || (mod.default && mod.default.LocalNotifications))) || null;
  } catch (e) { return null; }
}

// Create the Android notification channel once (idempotent). High importance so
// the system shows a heads-up banner; public visibility for the lock screen.
async function _ensureAndroidChannel(LN) {
  if (_channelReady || !LN || typeof LN.createChannel !== "function") return;
  try {
    await LN.createChannel({
      id: CRM_PUSH_CHANNEL_ID,
      name: "ARO CRM Notifications",
      importance: 5,   // IMPORTANCE_HIGH → heads-up banner
      visibility: 1,   // VISIBILITY_PUBLIC → show on lock screen
      vibration: true
    });
    _channelReady = true;
  } catch (e) { /* best-effort — schedule still works without an explicit channel */ }
}

// Post a system notification on Android foreground mirroring the FCM message.
// No-op if the plugin is unavailable. iOS must NOT call this (would duplicate).
async function _showAndroidLocalNotification(title, body, data) {
  try {
    var LN = await _getLocalNotifications();
    if (!LN || typeof LN.schedule !== "function") return;
    await _ensureAndroidChannel(LN);
    var d = _asObject(data);
    _localNotifSeq = (_localNotifSeq + 1) % 2000000000;
    await LN.schedule({
      notifications: [{
        id: _localNotifSeq,
        title: title || "",
        body: body || "",
        channelId: CRM_PUSH_CHANNEL_ID,
        // extra carries the open-lead routing data for the tap handler.
        extra: {
          type:   d.type   ? String(d.type)   : "",
          leadId: d.leadId ? String(d.leadId) : "",
          status: d.status ? String(d.status) : ""
        }
      }]
    });
  } catch (e) { /* never let a foreground receipt throw */ }
}

// POST the device token to the backend. Reads auth from the saved session.
// Exported so a re-register flow can push a refreshed token directly.
export async function sendTokenToBackend(token, platform) {
  if (!token) return;
  _lastToken = token;
  try { if (typeof window !== "undefined" && window.localStorage) localStorage.setItem("crm_push_token", token); } catch (e) {}
  var session = null;
  try {
    var raw = (typeof window !== "undefined" && window.localStorage)
      ? localStorage.getItem("crm_aro_session") : null;
    if (raw) session = JSON.parse(raw);
  } catch (e) { session = null; }
  if (!session || !session.token) return; // not logged in — nothing to associate
  // Stable per-install device id. Get-or-create so EVERY registration carries a
  // unique, non-empty deviceId — the backend uses it to keep one token per
  // device (dropping the stale token left by FCM rotation that caused duplicate
  // pushes). Previously this only READ the key, which was never set => always "".
  var deviceId = "";
  try {
    if (window.localStorage) {
      deviceId = localStorage.getItem("crm_device_id") || "";
      if (!deviceId) {
        deviceId = (window.crypto && crypto.randomUUID)
          ? crypto.randomUUID()
          : ("dev-" + Math.random().toString(36).slice(2) + Date.now().toString(36));
        localStorage.setItem("crm_device_id", deviceId);
      }
    }
  } catch (e) { deviceId = ""; }
  try {
    await fetch(API + "/api/users/push-token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer " + session.token
      },
      body: JSON.stringify({ token: token, platform: platform || "unknown", deviceId: deviceId })
    });
  } catch (e) { /* offline / transient — token re-syncs on next init */ }
}

// Deregister this device's token from the CURRENT user (logout cleanup).
// MUST be called BEFORE the session is cleared — it needs a valid bearer.
// Reads the cached token by default. The bearer is taken EXPLICITLY from the
// caller (loginToken — App.js holds it in state) and falls back to the stored
// session only when none is passed, so a caller that has already cleared
// storage can still deregister (owner-approved change, 2026-11-03).
export async function removeTokenFromBackend(token, loginToken) {
  var tok = token || _lastToken;
  if (!tok) return;
  var bearer = loginToken || "";
  if (!bearer) {
    var session = null;
    try {
      var raw = (typeof window !== "undefined" && window.localStorage)
        ? localStorage.getItem("crm_aro_session") : null;
      if (raw) session = JSON.parse(raw);
    } catch (e) { session = null; }
    bearer = (session && session.token) ? session.token : "";
  }
  if (!bearer) return; // not logged in — nothing to deregister
  try {
    await fetch(API + "/api/users/push-token", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer " + bearer
      },
      body: JSON.stringify({ token: tok })
    });
  } catch (e) { /* best-effort — token is also reclaimed on next user's login */ }
  try {
    _lastToken = "";
    if (typeof window !== "undefined" && window.localStorage) localStorage.removeItem("crm_push_token");
  } catch (e) {}
}

// Delete this device's FCM token ON THE DEVICE (owner ruling 2026-11-03): after
// a logout — explicit, or forced by a 401 — the device must be as if it never
// existed for the account, so the token itself is invalidated with Firebase,
// not only deregistered from the backend. Native only; a no-op on web. Clears
// the cached token so nothing stale is ever re-sent. The next login's
// initPushNotifications() mints a fresh token and registers it under the new
// session. Best-effort — the backend already refuses delivery to a token
// whose session is not live.
export async function deleteDeviceToken() {
  var Capacitor = await _getNativeCapacitor();
  if (!Capacitor) return; // web or non-native — no-op
  var FirebaseMessaging;
  try {
    var mod = await import("@capacitor-firebase/messaging");
    FirebaseMessaging = mod && (mod.FirebaseMessaging || (mod.default && mod.default.FirebaseMessaging));
  } catch (e) { return; }
  if (!FirebaseMessaging || typeof FirebaseMessaging.deleteToken !== "function") return;
  try { await FirebaseMessaging.deleteToken(); } catch (e) { /* best-effort */ }
  try {
    _lastToken = "";
    if (typeof window !== "undefined" && window.localStorage) localStorage.removeItem("crm_push_token");
  } catch (e) {}
}

// Request permission, register with APNs/FCM, wire listeners, and ship the
// token to the backend. Safe to call multiple times — guarded by _initialized.
export async function initPushNotifications() {
  var Capacitor = await _getNativeCapacitor();
  if (!Capacitor) return; // web or non-native — no-op
  if (_initialized) return;

  var FirebaseMessaging;
  try {
    var mod = await import("@capacitor-firebase/messaging");
    FirebaseMessaging = mod && (mod.FirebaseMessaging || (mod.default && mod.default.FirebaseMessaging));
  } catch (e) { return; }
  if (!FirebaseMessaging) return;

  try {
    var perm = await FirebaseMessaging.checkPermissions();
    var checkResult = perm && perm.receive;
    if (perm && perm.receive !== "granted") {
      perm = await FirebaseMessaging.requestPermissions();
    }
    console.log('[push] permission check:', checkResult || 'unknown', 'after request:', (perm && perm.receive) || 'unknown');
    if (!perm || perm.receive !== "granted") return; // user denied

    var platform = (typeof Capacitor.getPlatform === "function") ? Capacitor.getPlatform() : "unknown";

    // tokenReceived => fires with the FCM token (initial + on every refresh).
    // getToken() below covers the initial fetch; this keeps a rotated token in sync.
    _listeners.push(FirebaseMessaging.addListener("tokenReceived", function (event) {
      try { sendTokenToBackend(event && event.token, platform); } catch (e) {}
    }));
    // Foreground receipt — option (b): show the SYSTEM notification only, no
    // in-app banner. iOS surfaces the OS banner via presentationOptions
    // ["alert","badge","sound"] (capacitor.config.ts), so there's nothing to do
    // for iOS here. Android does NOT auto-display a foreground FCM notification,
    // so we post a local notification mirroring it (Android ONLY — scheduling on
    // iOS would duplicate the OS banner). Native-only listener; web never fires.
    _listeners.push(FirebaseMessaging.addListener("notificationReceived", function (event) {
      try {
        var n = (event && event.notification) || {};
        if (platform === "android") {
          _showAndroidLocalNotification(
            n.title ? String(n.title) : "",
            n.body  ? String(n.body)  : "",
            _asObject(n.data)
          );
        }
      } catch (e) { /* never let a foreground receipt throw */ }
    }));
    // Tap on the FCM/system notification (background / cold-start taps on iOS and
    // Android) → route to the lead via the durable open-lead bridge.
    _listeners.push(FirebaseMessaging.addListener("notificationActionPerformed", function (event) {
      try { _routeOpenLead(event && event.notification && event.notification.data); }
      catch (e) { /* never let tap handling throw */ }
    }));

    // Android foreground notifications are posted by @capacitor/local-notifications
    // (see notificationReceived above), so THEIR taps fire
    // localNotificationActionPerformed — NOT the FCM handler. Wire the same
    // open-lead routing for them, and pre-create the channel so the first
    // foreground push displays at high importance. Android-only.
    if (platform === "android") {
      var LN = await _getLocalNotifications();
      if (LN) {
        try { await _ensureAndroidChannel(LN); } catch (e) {}
        if (typeof LN.addListener === "function") {
          _listeners.push(LN.addListener("localNotificationActionPerformed", function (action) {
            try { _routeOpenLead(action && action.notification && action.notification.extra); }
            catch (e) { /* never let tap handling throw */ }
          }));
        }
      }
    }

    // Imperative initial fetch (replaces register() + the registration event).
    // Wrapped in its OWN try/catch — this is the error path the old
    // registrationError listener handled. A getToken() failure is swallowed
    // here: init still completes (_initialized becomes true below) and the
    // tokenReceived listener remains the fallback that delivers the token if/
    // when it later becomes available.
    try {
      var result = await FirebaseMessaging.getToken();
      if (result && result.token) sendTokenToBackend(result.token, platform);
    } catch (e) { console.error('[push] getToken failed on', platform || 'unknown', '-', (e && e.message) || e); }

    _initialized = true;
  } catch (e) { /* registration failed — leave _initialized false so a later call retries */ }
}

// Remove all listeners registered by initPushNotifications(). Idempotent.
export async function disposePushNotifications() {
  var pending = _listeners.slice();
  _listeners = [];
  _initialized = false;
  for (var i = 0; i < pending.length; i++) {
    try {
      var handle = await pending[i];
      if (handle && typeof handle.remove === "function") await handle.remove();
    } catch (e) { /* ignore */ }
  }
}
